/**
 * Advisor Selector functionality
 * Handles the advisor selection dropdown in the header
 * Integrated with existing advisor system from jsc-reset-scripts.js
 */

class AdvisorSelector {
  constructor() {
    this.selector = document.querySelector('[data-advisor-selector]');
    this.trigger = document.querySelector('[data-advisor-trigger]');
    this.dropdown = document.querySelector('[data-advisor-dropdown]');
    this.idInput = document.querySelector('[data-advisor-id-input]');
    this.nameInput = document.querySelector('[data-advisor-name-input]');
    this.profilePictureInput = document.querySelector('[data-advisor-profile-picture-input]');
    this.container = document.querySelector('.advisor-selector__container');

    this.isOpen = false;
    this.triggerClickHandler = null;
    this.documentClickHandler = null;

    this.init();
  }

  init() {
    if (!this.selector) return;

    this.bindEvents();
    this.validateAndUpdateCartAttributes();
  }

  bindEvents() {
    // Remove existing event listeners to avoid duplicates
    if (this.triggerClickHandler) {
      this.trigger?.removeEventListener('click', this.triggerClickHandler);
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }

    // Toggle dropdown
    this.triggerClickHandler = (e) => {
      e.preventDefault();
      this.toggleDropdown();
    };
    this.trigger?.addEventListener('click', this.triggerClickHandler);

    // Close dropdown when clicking outside
    this.documentClickHandler = (e) => {
      if (!this.selector.contains(e.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  validateAndUpdateCartAttributes() {
    // Get current values from hidden inputs (which reflect cart attributes)
    const currentCartId = this.idInput?.value || '';
    const currentCartName = this.nameInput?.value || '';
    const currentCartProfile = this.profilePictureInput?.value || '';

    // Get current values from localStorage
    const localStorageId = localStorage.getItem('Advisor ID') || '';
    const localStorageName = localStorage.getItem('Advisor Name') || '';
    const localStorageProfile = localStorage.getItem('Advisor Profile Picture') || '';

    // Prioritize localStorage over cart attributes since localStorage is the source of truth
    // Only use cart attributes if localStorage is empty
    const effectiveId = (localStorageId && localStorageId !== '-' && localStorageId !== 'null' && localStorageId !== 'undefined') 
      ? localStorageId : (currentCartId && currentCartId !== '-' && currentCartId !== 'null' && currentCartId !== 'undefined' ? currentCartId : '');
    const effectiveName = (localStorageName && localStorageName !== '-' && localStorageName !== 'null' && localStorageName !== 'undefined') 
      ? localStorageName : (currentCartName && currentCartName !== '-' && currentCartName !== 'null' && currentCartName !== 'undefined' ? currentCartName : '');
    const effectiveProfile = (localStorageProfile && localStorageProfile !== '-' && localStorageProfile !== 'null' && localStorageProfile !== 'undefined') 
      ? localStorageProfile : (currentCartProfile && currentCartProfile !== '-' && currentCartProfile !== 'null' && currentCartProfile !== 'undefined' ? currentCartProfile : '');

    // Check if there's a mismatch between effective values and localStorage
    // Only update if localStorage is empty and we have values from cart attributes
    const needsUpdate =
      (!localStorageId && effectiveId) ||
      (!localStorageName && effectiveName) ||
      (!localStorageProfile && effectiveProfile);

    if (needsUpdate && effectiveId && effectiveName) {
      console.log('Syncing from cart attributes to localStorage...', {
        cart: { id: currentCartId, name: currentCartName, profile: currentCartProfile },
        effective: { id: effectiveId, name: effectiveName, profile: effectiveProfile },
        localStorage: { id: localStorageId, name: localStorageName, profile: localStorageProfile }
      });

      // Use the centralized updateAdvisorData function if available
      if (typeof updateAdvisorData === 'function') {
        updateAdvisorData(effectiveId, effectiveName, effectiveProfile, false);
      } else {
        localStorage.setItem('Advisor ID', effectiveId);
        localStorage.setItem('Advisor Name', effectiveName);
        localStorage.setItem('Advisor Profile Picture', effectiveProfile);
        var storepointID = window.location.href.split('selected=')[1];
        localStorage.setItem('Advisor Storepoint ID', storepointID);

        // Update cart attributes with effective values
        // const attributes = {
        //   'Advisor ID': effectiveId,
        //   'Advisor Name': effectiveName
        //   // 'Advisor Profile Picture': effectiveProfile
        // };

        // CartJS.setAttributes(attributes);
      }
    }
    this.updateDisplayFromLocalStorage();
  }

  updateDisplayFromLocalStorage() {
    const advisorId = localStorage.getItem('Advisor ID');
    const advisorName = localStorage.getItem('Advisor Name');
    const advisorProfilePicture = localStorage.getItem('Advisor Profile Picture') || '';

    if (advisorId && advisorName) {
      this.updateDisplay(advisorId, advisorName, advisorProfilePicture);
    } else {
      //this.showNoAdvisorState(); //commented out by Nurina 7/8/25 to display the dropdown by default before selecting advisor
    }
  }

  updateDisplay(id, name, profilePicture) {
    // Update hidden inputs
    if (this.idInput) this.idInput.value = id;
    if (this.nameInput) this.nameInput.value = name;
    if (this.profilePictureInput) this.profilePictureInput.value = profilePicture;

    // Update button text
    const currentElement = this.trigger?.querySelector('.advisor-selector__current');
    if (currentElement) {
      let profileImgHtml = '';
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class="advisor-selector__profile-picture"><img src="${profilePicture}" alt="${name}" width="40" height="40" loading="lazy"></div>`;
      }
      else {
        profileImgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
              <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
            </svg>`;
      }

      currentElement.innerHTML = `
        ${profileImgHtml}
      `;
      //BACKUP original content
      // currentElement.innerHTML = `
      //   ${profileImgHtml}
      //   <div class="advisor-selector__info">
      //     <strong>${name}</strong>
      //     <span class="advisor-selector__id">(ID: ${id})</span>
      //   </div>
      // `;
    }

    // Update cart display if it exists
    const cartAdvisorName = document.getElementById('jsc-cart-advisor-name');
    if (cartAdvisorName) {
      cartAdvisorName.textContent = name;
    }

    const cartAdvisorNameInput = document.getElementById('jsc-cart-advisor-name-input');
    if (cartAdvisorNameInput) {
      cartAdvisorNameInput.value = name;
    }

    // Ensure we're showing the dropdown state
    this.showDropdownState(profilePicture, name, id);
  }

  showDropdownState(profilePicture, name, id) {
    if (!this.container) return;

    // Remove no-advisor state if it exists
    const noAdvisorElement = this.container.querySelector('.advisor-selector__no-advisor');
    if (noAdvisorElement) {
      noAdvisorElement.remove();
    }

    // Ensure dropdown state exists
    if (!this.container.querySelector('.advisor-selector__button')) {
      const advisorPage = "choose-from-your-nearest-advisors?selected=''";
      const advisorPageValue = advisorPage ? advisorPage : 'choose-from-your-nearest-advisors';
      // Use passed parameters, fallback to localStorage if not provided
      const advisorName = name || localStorage.getItem('Advisor Name') || '';
      const advisorId = id || localStorage.getItem('Advisor ID') || '';
      let profileImgHtml = '';
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class=\"advisor-selector__profile-picture\"><img src=\"${profilePicture}\" alt=\"${advisorName}\" width=\"40\" height=\"40\" loading=\"lazy\"></div>`;
      }
      const dropdownHTML = `
        <button
          type="button"
          class="advisor-selector__button"
          aria-expanded="false"
          aria-controls="advisor-dropdown"
          data-advisor-trigger
        >
          <span class="advisor-selector__current">
            ${profileImgHtml}
            <div class="advisor-selector__info">
              <strong>${advisorName}</strong>
              <span class="advisor-selector__id">(ID: ${advisorId})</span>
            </div>
          </span>
          <span class="advisor-selector__icon">
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon--wide icon-chevron-down icon--line" viewbox="0 0 28 16" width="20" height="20">
              <path d="M1.57 1.59l12.5 12.5L26.57 1.59" stroke="currentColor" fill="none" stroke-width="1.5"/>
            </svg>
          </span>
        </button>

        <div class="advisor-selector__dropdown" id="advisor-dropdown" data-advisor-dropdown>
          <div class="advisor-selector__actions">
          <a href="/pages/${advisorPageValue}" class="advisor-selector__link">
        ${window.theme?.advisor?.advisor_selector?.change_advisor || 'Change studio'}
      </a>
          </div>
        </div>
      `;

      this.container.innerHTML = dropdownHTML;

      // Re-bind events for new elements
      this.trigger = this.container.querySelector('[data-advisor-trigger]');
      this.dropdown = this.container.querySelector('[data-advisor-dropdown]');

      this.bindEvents();
    }
    else {
      // update dropdown with the latest advisor 
      const advisorPage = "choose-from-your-nearest-advisors?selected=''";
      const advisorPageValue = advisorPage ? advisorPage : 'choose-from-your-nearest-advisors';
      // Use passed parameters, fallback to localStorage if not provided
      const advisorName = name || localStorage.getItem('Advisor Name') || '';
      const advisorId = id || localStorage.getItem('Advisor ID') || '';
      let storepointId = localStorage.getItem('Advisor Storepoint ID');
      

      let profileImgHtml = localStorage.getItem('Advisor Profile Picture') || '';
      console.log('update dropdown with the latest advisor',advisorName,advisorId);
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class=\"advisor-selector__profile-picture\"><img src=\"${profilePicture}\" alt=\"${name}\" width=\"40\" height=\"40\" loading=\"lazy\"></div>`;
      }
      else {
        profileImgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
              <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
            </svg>`;
      }

      const dropdownHTML = `
      <div class="advisor-selector__actions">
        <a href="/pages/storepoint-vn/id_${advisorId}" class="advisor-selector__link">
        ${profileImgHtml}
        <div class="advisor-selector__info m-l-10">
          <strong>${advisorName}</strong>
          <span class="advisor-selector__id">Thermomix® Advisor</span>
        </div>
        </a>
      </div>
      <div class="advisor-selector__actions">
        <a href="/pages/${advisorPageValue}?selected=${storepointId}" class="advisor-selector__link">
        ${window.theme?.advisor?.advisor_selector?.change_advisor || 'Đổi studio'}
        </a>
      </div>
      `;

      this.dropdown.innerHTML = dropdownHTML; //this part is used to apply the latest dropdown content

      // // Re-bind events for new elements
      // this.trigger = this.container.querySelector('[data-advisor-trigger]');
      // this.dropdown = this.container.querySelector('[data-advisor-dropdown]');

      // this.bindEvents();
    }
  }

  showNoAdvisorState() {
    if (!this.container) return;
    // Remove dropdown state if it exists
    const buttonElement = this.container.querySelector('.advisor-selector__button');
    if (buttonElement) {
      buttonElement.remove();
    }

    const dropdownElement = this.container.querySelector('.advisor-selector__dropdown');
    if (dropdownElement) {
      dropdownElement.remove();
    }

    // Ensure no-advisor state exists
    if (!this.container.querySelector('.advisor-selector__no-advisor')) {
      const advisorPage = "choose-from-your-nearest-advisors?selected=''";
      const advisorPageValue = advisorPage ? advisorPage : 'choose-from-your-nearest-advisors';
      let defaultAdvisorPage = document.querySelector('.jsc-pages-storepoint').value;

      const noAdvisorHTML = `
          <button type="button" class="advisor-selector__button" aria-expanded="false" aria-controls="advisor-dropdown" data-advisor-trigger>
            <span class="advisor-selector__current">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
                <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
              </svg>
            </span>

            <span class="site-nav__icon-label ">
              Advisor
            </span>
          </button>
          <div class="advisor-selector__dropdown" id="advisor-dropdown" data-advisor-dropdown>
            <div class="advisor-selector__actions">
                <a href="${defaultAdvisorPage}" class="advisor-selector__link">
                ${window.theme?.advisor?.advisor_selector?.my_advisor || 'My advisor'}
              </a>
              
            </div>
            <div class="advisor-selector__actions">
              <a href="/pages/${advisorPageValue}" class="advisor-selector__link">
              ${window.theme?.advisor?.advisor_selector?.select_your_advisor || 'Select studio'}
              </a>
            </div>
          </div>
      `;
      //BACKUP original content
      // const noAdvisorHTML = `
      //   <div class="advisor-selector__no-advisor">
      //     <a href="/pages/${advisorPageValue}" class="advisor-selector__select-link">
      //       <span class="advisor-selector__placeholder">Please Select a advisor</span>
      //       <span class="advisor-selector__icon">
      //         <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon--wide icon-chevron-right icon--line" viewbox="0 0 28 16" width="20" height="20">
      //           <path d="M1.57 1.59l12.5 12.5L26.57 1.59" stroke="currentColor" fill="none" stroke-width="1.5"/>
      //         </svg>
      //       </span>
      //     </a>
      //   </div>
      // `;

      this.container.innerHTML = noAdvisorHTML;

      // Re-bind events for new elements
      this.trigger = this.container.querySelector('[data-advisor-trigger]');
      this.dropdown = this.container.querySelector('[data-advisor-dropdown]');
      this.bindEvents();
    }
  }



  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (!this.dropdown || !this.trigger) return;

    this.dropdown.classList.add('is-active');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('advisor-modal-open');
    this.isOpen = true;
  }

  closeDropdown() {
    if (!this.dropdown || !this.trigger) return;

    this.dropdown.classList.remove('is-active');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('advisor-modal-open');
    this.isOpen = false;
  }
}

// Initialize advisor selector when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.advisorSelectorInstance = new AdvisorSelector();
});

document.addEventListener('advisor:selected', function(e) {
  const { id, name, profileImg } = e.detail;

  // Get the AdvisorSelector instance and use its methods
  const advisorSelector = window.advisorSelectorInstance;
  if (advisorSelector) {
    console.log('AdvisorSelector instance is available');
    // Update display
    advisorSelector.updateDisplay(id, name, profileImg);
    // Close dropdown
    advisorSelector.closeDropdown();
  } else {
    console.log('AdvisorSelector instance is not available');
  }
});

document.addEventListener('advisor:cleared', function(e) {
  // Get the AdvisorSelector instance and use its methods
  const advisorSelector = window.advisorSelectorInstance;
  if (advisorSelector) {
    console.log('AdvisorSelector: Advisor data cleared, updating display');
    // Clear hidden inputs
    if (advisorSelector.idInput) advisorSelector.idInput.value = '';
    if (advisorSelector.nameInput) advisorSelector.nameInput.value = '';
    if (advisorSelector.profilePictureInput) advisorSelector.profilePictureInput.value = '';

    // Show no advisor state
    advisorSelector.showNoAdvisorState();

    // Close dropdown if open
    advisorSelector.closeDropdown();
  } else {
    console.log('AdvisorSelector instance is not available');
  }
});

// Export for use in other modules
window.AdvisorSelector = AdvisorSelector;